import { Template } from "@/types/template";
import useSWR from "swr";
import { useMemo, useState } from "react";
import { getTagLabel, ListTemplateCard } from "@/components/launch/ListTemplateCard";
import { bool, nonFalse, str } from "@/lib/utils";
import { useRouter } from "next/router";
import { ScrollToTop } from "@/components/landing/ScrollToTop";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Search, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { parseAsString, useQueryState } from "nuqs";

const priorityTags = ["oss", "popular", "template"];

export const ListTemplates = ({ templates: initialTemplates }: { templates: Template[] }) => {
  const router = useRouter();
  const skipCache = bool(router.query.skipCache);
  const { data: templates } = useSWR<Template[]>(
    `/api/template?skipCache=${skipCache}`,
    null,
    {
      fallbackData: initialTemplates
    }
  );
  const experimental = bool(router.query.experimental);
  const setExperimental = (value: boolean) => {
    router.push({
      query: { ...router.query, experimental: value ? "1" : undefined, tag: undefined }
    });
  };

  const selectedTag = str(router.query.tag);

  const setSelectedTag = (tag: string | undefined) => {
    router.push({
      query: { ...router.query, tag }
    });
  };

  const [allTags, setAllTags] = useState(false);
  const maxShow = 5;
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));

  const tags = useMemo(() => {
    const tags = new Set<string>(
      templates
        ?.filter(t => (experimental ? true : !t.experimental))
        .map(t => t.tags || [])
        .flat()
    );
    if (selectedTag) {
      tags.add(selectedTag);
    }
    priorityTags.forEach(tag => tags.add(tag));
    const sorted = Array.from(tags).sort();
    return [
      ...priorityTags,
      selectedTag && priorityTags.includes(selectedTag) ? undefined : selectedTag,
      ...sorted.filter(t => !priorityTags.includes(t) && t !== selectedTag)
    ]
      .filter(nonFalse)
      .filter(t => t !== "");
  }, [templates, experimental, selectedTag]);

  const filteredTemplates = templates?.filter(template => {
    const experimentalMatch = experimental ? true : !template.experimental;
    const tagMatch = selectedTag ? template.tags?.includes(selectedTag) : true;
    return experimentalMatch && tagMatch;
  });

  const searchedTemplates = useMemo(() => {
    if (!search) {
      return filteredTemplates;
    }
    const q = search.toLowerCase();
    return filteredTemplates?.filter(
      template =>
        template.name.toLowerCase().includes(q) ||
        template.description?.toLowerCase().includes(q) ||
        template.tags?.some(tag => tag.toLowerCase().includes(q)) ||
        template.intro.toLowerCase().includes(q) ||
        template.id.toLowerCase().includes(q)
    );
  }, [filteredTemplates, search]);

  return (
    <div className="px-4 container pt-16 md:pt-24">
      <ScrollToTop />
      <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">Templates</h1>

      <p className="text-center mb-8 text-lg md:text-xl text-muted-foreground text-balance">
        Deploy new application in just a few clicks. Launch a new project from a boilerplate or
        run an open-source application on new or existing server.
      </p>
      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Search className="w-5 h-5" aria-hidden="true" />
          </span>
          <Input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            className="pl-10"
            autoFocus
          />
        </div>
      </div>

      <div className="gap-4 flex flex-wrap justify-center">
        <div className="flex flex-row justify-between gap-4 col-span-4 pt-4 pb-4 border-b basis-full">
          {tags.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant="outline"
                  className={cn(
                    "px-2 py-1 text-sm hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors",
                    !selectedTag && "text-primary"
                  )}
                  onClick={() => setSelectedTag(undefined)}
                >
                  All
                </Badge>

                {tags.slice(0, allTags ? tags.length : maxShow).map(tag => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className={cn(
                      "px-2 py-1 text-sm hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors",
                      selectedTag === tag && "text-primary"
                    )}
                    onClick={() => {
                      setSelectedTag(selectedTag === tag ? undefined : tag);
                      setAllTags(false);
                    }}
                  >
                    {getTagLabel(tag)}
                  </Badge>
                ))}

                {tags.length > maxShow && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "px-2 py-1 text-sm cursor-pointer hover:bg-primary/30 transition-colors"
                    )}
                    onClick={() => setAllTags(!allTags)}
                  >
                    {allTags ? "Show less" : "Show more"}
                  </Badge>
                )}
              </div>
            </div>
          )}

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                aria-haspopup="true"
                size="icon"
                variant="secondary"
                className="h-8"
                animations="none"
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Show</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setExperimental(!experimental)}
                className="flex gap-2"
              >
                <Checkbox
                  id="experimental"
                  checked={experimental}
                  onCheckedChange={checked => setExperimental(checked as boolean)}
                />
                Experimental
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {searchedTemplates && searchedTemplates.length === 0 ? (
          <div className="w-full text-center text-muted-foreground py-12 text-lg flex flex-row items-center justify-center gap-2">
            <SearchX className="w-6 h-6" /> Nothing found. Maybe try turning on experimental?
          </div>
        ) : (
          searchedTemplates?.map(template => (
            <ListTemplateCard key={template.id} template={template} />
          ))
        )}
      </div>
    </div>
  );
};
